// Три полосы главной страницы: день, неделя, цель.
//
// Полоса дня отвечает «сколько я сегодня сделал», недельная — «двигаюсь ли я к
// цели», целевая — «сколько всего пройдено». Три разных вопроса и три разных
// окна: сегодня, две недели, весь путь до 16 июня.
//
// **Цвет меняется скачком на пороге, а не градиентом.** Смысл полосы в том,
// ГДЕ порог: перешёл минимум, дошёл до цели, взял сверх. Плавный переход ровно
// это и прячет — «я дошёл до цели или почти» глазом не отличается. Поэтому у
// дневной полосы видимые засечки и три зоны, а не заливка от жёлтого к
// зелёному.
//
// Чего здесь нет: прогноза балла (решение 15 — он ждёт калибровки фазы 5),
// значков, серий подряд и слов похвалы. Оценочные слова заменены положением
// относительно коридора: «в коридоре» верно всегда, а «замечательно» на вызове
// пришлось бы выдавать за 60% верных — то есть за нижнюю границу нормы.

import type { CorridorStatus, ProgressSnapshot } from "../api";
import { formatDay, plural } from "../lib/dates";

/**
 * Темп — целым числом навыков в неделю, а не дробью.
 *
 * Дробей на этом экране нет вовсе, и не из вкусовщины: числа идут в `.num`,
 * то есть в монофонте, а в нём запятая занимает целое знакоместо — «3,9»
 * читается как «3 , 9». Видно на скриншоте полос от 3 сентября 2026.
 *
 * Целое здесь ещё и точнее по смыслу: «нужно около 4 навыков в неделю»
 * говорит то же, что «3,9», и не обещает точности, которой в оценке нет.
 */
function perWeek(value: number): number {
  if (value <= 0) return 0;
  return Math.max(1, Math.round(value));
}

/**
 * Прирост за окно со знаком: «+3», «0», «−1». Экспортируется: тем же
 * числом шапка «Прогресса» отвечает на тот же вопрос, и две копии формата
 * разошлись бы на первом же минусе.
 *
 * У нуля знака нет: «+0» читается как достижение там, где его не было.
 * Отрицательный прирост возможен — забывание опускает владение ниже порога, —
 * и прятать его нельзя: полоса, которая умеет только расти, перестаёт значить
 * что-либо уже на второй просадке.
 */
export function growth(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `\u2212${-n}` : "0";
}

/** Доля в процентах, обрезанная по краям. */
function pct(value: number, scale: number): number {
  if (scale <= 0) return 0;
  return Math.max(0, Math.min(100, (value / scale) * 100));
}

/** Одна строка: подпись, число справа, полоса под ними. */
function Row({
  label,
  value,
  children,
  note,
}: {
  label: string;
  value: React.ReactNode;
  children: React.ReactNode;
  note?: React.ReactNode;
}) {
  return (
    <div className="meter">
      <div className="meter__head">
        <span className="meter__label">{label}</span>
        <span className="meter__value num">{value}</span>
      </div>
      {children}
      {note ? <div className="meter__note">{note}</div> : null}
    </div>
  );
}

/**
 * Полоса дня. Шкала — минуты; три порога приходят с сервера из плана дня.
 *
 * Длина дорожки берётся с запасом над целью, иначе зона «сверх набора» не
 * поместилась бы: набор, закрытый ровно в цель, занял бы всю полосу, и добавка
 * рисовалась бы за её пределами.
 */
export function DayMeter({
  spentMinutes,
  goalMinutes,
  minMinutes,
  tasksDone,
  tasksTotal,
}: {
  spentMinutes: number;
  goalMinutes: number;
  minMinutes: number | null;
  tasksDone: number;
  tasksTotal: number;
}) {
  // Набор пуст — до выбора навыка в нём нечего делать. Тогда порогов нет
  // вовсе: цель 0, а минимум 8 был бы порогом ВЫШЕ обещания, то есть
  // требованием, которого система не выдвигала.
  const empty = goalMinutes <= 0;
  // Минимум показывается только если он ниже цели. В коротком дне он равен ей,
  // и `minMinutes` там уже `null`; страховка здесь — на случай набора короче
  // восьми минут, который планировщик собрать может.
  const showMin = !empty && minMinutes !== null && minMinutes < goalMinutes;
  const scale = Math.max(goalMinutes * 1.5, spentMinutes, 1);
  const below = showMin && spentMinutes < (minMinutes ?? 0);
  const reached = !empty && spentMinutes >= goalMinutes;
  const zone = empty ? "mid" : below ? "low" : reached ? "over" : "mid";
  return (
    <Row
      label="Сегодня"
      value={
        <>
          {spentMinutes} / {goalMinutes} мин
        </>
      }
      note={
        <>
          {empty ? (
            "Набор соберётся, когда выберешь новый навык"
          ) : zone === "low" ? (
            <>
              До минимума <span className="num">{(minMinutes ?? 0) - spentMinutes}</span>{" "}
              {plural((minMinutes ?? 0) - spentMinutes, "минута", "минуты", "минут")}
            </>
          ) : zone === "mid" ? (
            <>
              {/* «Минимум взят» только когда минимум есть и он позади. У набора
                  ровно в восемь минут минимум совпадает с целью, и средней зоны
                  нет вовсе — тогда эта фраза была бы неправдой при нуле. */}
              {showMin ? "Минимум взят · до набора " : "До набора "}
              <span className="num">{goalMinutes - spentMinutes}</span>{" "}
              {plural(goalMinutes - spentMinutes, "минута", "минуты", "минут")}
            </>
          ) : (
            <>
              Набор закрыт
              {spentMinutes > goalMinutes ? (
                <>
                  {" "}
                  · сверх <span className="num">{spentMinutes - goalMinutes}</span>{" "}
                  {plural(spentMinutes - goalMinutes, "минута", "минуты", "минут")}
                </>
              ) : null}
            </>
          )}
          {tasksTotal > 0 ? (
            <>
              {" · "}
              <span className="num">{tasksDone}</span> из{" "}
              <span className="num">{tasksTotal}</span>{" "}
              {/* Родительный падеж: «из 1 задачи», «из 3 задач». Именительный
                  дал бы «0 из 3 задачи». */}
              {plural(tasksTotal, "задачи", "задач", "задач")}
            </>
          ) : null}
        </>
      }
    >
      <div className="bar bar--tall">
        <div className={`bar__fill bar__fill--${zone}`} style={{ width: `${pct(spentMinutes, scale)}%` }} />
        {/* Засечка минимума. В коротком дне её нет: обещание набора и есть
            минимум, и две засечки в одной точке читались бы как сбой. */}
        {showMin ? (
          <div className="bar__tick" style={{ left: `${pct(minMinutes ?? 0, scale)}%` }} />
        ) : null}
        {empty ? null : (
          <div className="bar__tick bar__tick--goal" style={{ left: `${pct(goalMinutes, scale)}%` }} />
        )}
      </div>
    </Row>
  );
}

const CORRIDOR_NAME: Record<string, string> = {
  review: "повторение",
  new: "новый навык",
  challenge: "вызов",
};

const STATUS_WORD: Record<CorridorStatus, string> = {
  below: "ниже коридора",
  in: "в коридоре",
  above: "выше коридора",
  unknown: "мало данных",
};

/**
 * Недельная полоса: дни занятий в скользящем окне.
 *
 * Цель окна — из обязательства ученика, а не константа 18: выбрал четыре дня в
 * неделю — мера «12 из последних 21». В решении 14 ценно скользящее окно, а не
 * число: пропуск дня почти не вредит, вредит разрыв серии подряд.
 */
export function WeekMeter({ data }: { data: ProgressSnapshot }) {
  const { window: w, tempo } = data;
  const kept = w.days >= w.target;
  // Окно скользящее: в первый день занятых дней в нём ноль по построению, а не
  // по вине ученика. Сказать «отстаёшь от своего обязательства» через десять
  // секунд после того, как он его взял, — это соврать в первую же минуту, и
  // мера, которая врёт на старте, дальше не читается вовсе.
  //
  // Судим не раньше, чем окно наберёт неделю истории. До этого — сколько дней
  // уже занято, без вердикта.
  const judged = data.learnerDays > 7;
  return (
    <Row
      label="Три недели"
      value={
        <>
          {w.days} / {w.target} дней
        </>
      }
      note={
        <>
          <div>
            {w.daysPerWeek} {plural(w.daysPerWeek, "день", "дня", "дней")} в неделю
            {judged ? (
              <> · {kept ? "обязательство держится" : "отстаёшь от своего обязательства"}</>
            ) : (
              <> · идёт первая неделя, окно ещё набирается</>
            )}
          </div>
          <div style={{ marginTop: 4 }}>
            Темп: нужно около{" "}
            <span className="num">{perWeek(tempo.needPerWeek)}</span>{" "}
            {plural(perWeek(tempo.needPerWeek), "навыка", "навыков", "навыков")} в
            неделю · за <span className="num">{tempo.recentDays}</span>{" "}
            {plural(tempo.recentDays, "день", "дня", "дней")} открыто{" "}
            <span className="num">{tempo.openedRecent}</span>
          </div>
          <div className="chips" style={{ marginTop: 8 }}>
            {data.corridors.map((c) => (
              <span key={c.kind} className={`tag tag--corridor tag--${c.status}`}>
                {CORRIDOR_NAME[c.kind]}:{" "}
                {c.status === "unknown" ? (
                  STATUS_WORD.unknown
                ) : (
                  <>
                    <span className="num">
                      {Math.round((c.correct / c.tasks) * 100)}
                    </span>
                    % · {STATUS_WORD[c.status]}
                  </>
                )}
              </span>
            ))}
          </div>
        </>
      }
    >
      <div className="bar bar--tall">
        <div
          className={`bar__fill bar__fill--${kept ? "over" : "mid"}`}
          style={{ width: `${pct(w.days, w.of)}%` }}
        />
        <div className="bar__tick bar__tick--goal" style={{ left: `${pct(w.target, w.of)}%` }} />
      </div>
    </Row>
  );
}

/**
 * Целевая полоса: вся карта, 226 навыков, и движение по ней за окно.
 *
 * **Числитель — пройденное, а не доведённое до мастерства.** До 11 сентября
 * 2026 экраны давали четыре разных ответа на вопрос «сколько я прошёл»:
 * открыто 43, доведено 18, пройдено маршрутом 31, задачи есть у 164. Ни один
 * не был помечен как главный, и прогресса не было видно именно поэтому.
 * Единица теперь одна — навык пройден, если открыт и освоен не хуже 0,75, тем
 * же порогом, которым планировщик признаёт предпосылку готовой. Мастерство
 * осталось вторым слоем полосы, открытое — числом в подписи.
 *
 * **Под полосой — прирост, а не только состояние.** «43 из 164» одинаково и
 * вчера, и сегодня; на вопрос «продвинулся ли я» оно не отвечает, и полоса дня
 * тоже — та меряет минуты, то есть усилие. Прирост считается по снимкам начала
 * дня, которые система и так пишет.
 *
 * Требуемый темп при этом считается по покрытому банком, и подпись говорит это
 * прямо. Иначе темп будет расти от добавленных шаблонов, а не от учёбы, и
 * первое же такое расхождение прочтётся как поломка.
 */
export function GoalMeter({ data }: { data: ProgressSnapshot }) {
  const { skills, daysToExam, progress } = data;
  const mastered = pct(skills.mastered, skills.total);
  const started = pct(skills.passed - skills.mastered, skills.total);
  // Недельный прирост показывается, только если самый ранний снимок окна
  // старше сегодняшнего. Иначе это ровно то же число, что «сегодня», под
  // другим словом — а два одинаковых числа рядом читаются как ошибка.
  const week =
    progress.week !== null && progress.weekFrom !== null && progress.weekFrom !== data.date
      ? { value: progress.week, from: progress.weekFrom }
      : null;
  return (
    <Row
      label="Вся карта"
      value={
        <>
          {skills.passed} / {skills.total}
        </>
      }
      note={
        <>
          <div>
            Пройдено — дальше можно строить. Доведено до мастерства{" "}
            <span className="num">{skills.mastered}</span> · открыто{" "}
            <span className="num">{skills.open}</span> · до экзамена{" "}
            <span className="num">{daysToExam}</span>{" "}
            {plural(daysToExam, "день", "дня", "дней")}
          </div>
          <div style={{ marginTop: 4 }}>
            {/*
              Снимка нет — значит ученик сегодня не открывал набор, и «+0»
              было бы утверждением без основания. Своих слов для этого случая
              не надо: строка просто говорит, откуда берётся движение.
            */}
            {progress.today === null ? (
              "Движение считается с первого за день набора"
            ) : (
              <>
                Сегодня <span className="num">{growth(progress.today)}</span>{" "}
                {plural(Math.abs(progress.today), "навык", "навыка", "навыков")}
                {week === null ? null : (
                  <>
                    {" · с "}
                    {formatDay(week.from)}{" "}
                    <span className="num">{growth(week.value)}</span>
                  </>
                )}
              </>
            )}
          </div>
          {/*
            Оговорка про покрытие показывается только когда она правда.
            Темп считается по навыкам, у которых есть задачи; когда их меньше,
            чем в карте, это надо сказать — иначе темп будет расти от
            добавленных шаблонов, а не от учёбы, и расхождение прочтётся как
            поломка. Когда покрытие полное (сейчас 226 из 226), строка
            превращается в шум и не рисуется.
          */}
          {data.tempo.covered < data.tempo.mapTotal ? (
            <div style={{ marginTop: 4 }}>
              Задачи готовы у <span className="num">{data.tempo.covered}</span>{" "}
              навыков — по ним и считается темп
            </div>
          ) : null}
        </>
      }
    >
      <div className="bar bar--tall">
        <div className="bar__base" style={{ width: `${mastered}%` }} />
        <div className="bar__delta" style={{ width: `${started}%` }} />
      </div>
    </Row>
  );
}
